import { useEffect, useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api, { ApiError } from '../lib/apiClient';
import { IconButton } from '../components/IconButton';
import { IconBack, IconTrash } from '../components/icons';
import { useAuth } from '../auth/useAuth';
import { labelFor, useConfig } from '../context/ConfigContext';
import { useConfirm } from '../context/ConfirmContext';
import { useToast } from '../context/ToastContext';
import { formatDateTime, formatMoney } from '../utils/format';

// TS port of the source (Falah) app's pages/ProcurementDetail.jsx.
//
// `status` is a fixed approval-chain state (PurchaseRequestController::
// STEP_KEYS/finalize()), not list-item-backed, so STATUS_LABELS stays a
// hardcoded map like the list page. One addition vs. source: a "Purchase
// order" link when `purchase_order` is present — the backend already eager-
// loads it on show() (PurchaseRequestController::show()), the source app's
// own ProcurementDetail.jsx just never surfaced it.
const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  pending_approval: 'Pending Approval',
  pending_second_approval: 'Pending Second Approval',
  approved: 'Approved',
  rejected: 'Rejected',
};

const STEP_LABELS: Record<string, string> = {
  first: 'First approval',
  second: 'Second approval',
};

function statusBadgeClass(status: string): string {
  if (status === 'approved') return 'badge-normal';
  if (status === 'rejected') return 'badge-important';
  return 'badge-neutral';
}

interface RequestDocument {
  id: number;
  original_name: string;
  url: string;
}

interface Approval {
  id: number;
  step: string;
  approver: { full_name: string } | null;
  decision: string;
  comment: string | null;
  decided_at: string;
}

interface RfqResponse {
  id: number;
  supplier: { id: number; business_name: string } | null;
  supplier_name: string | null;
  quoted_amount: string;
  received_at: string | null;
  is_selected: boolean;
}

interface RfqIssuance {
  id: number;
  rfq_number: string;
  issued_at: string;
  due_date: string | null;
  issued_by: { full_name: string } | null;
  responses: RfqResponse[];
}

interface PurchaseRequestRecord {
  id: number;
  reference: string;
  title: string;
  item_name: string;
  request_type: string | null;
  description: string | null;
  estimated_cost: number;
  quantity: number | null;
  purpose: string | null;
  status: string;
  final_outcome: string | null;
  final_comments: string | null;
  created_at: string;
  requester: { full_name: string } | null;
  documents: RequestDocument[];
  approvals: Approval[];
  purchase_order: { id: number; po_number: string } | null;
}

export function ProcurementDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { can } = useAuth();
  const { settings, lists } = useConfig();
  const confirmDelete = useConfirm();
  const { showError } = useToast();
  const fmtMoney = (n: number) => formatMoney(n, settings.currency);
  const [pr, setPr] = useState<PurchaseRequestRecord | null>(null);
  const [rfqs, setRfqs] = useState<RfqIssuance[]>([]);
  const [actionError, setActionError] = useState('');
  const [comment, setComment] = useState('');
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [rfqDueDate, setRfqDueDate] = useState('');
  const [issuingRfq, setIssuingRfq] = useState(false);
  const [responseForms, setResponseForms] = useState<Record<number, { supplier_name: string; quoted_amount: string }>>(
    {}
  );

  const refreshRfqs = () =>
    api.get<RfqIssuance[]>(`/purchase-requests/${id}/rfq-issuances`).then((res) => setRfqs(res.data));

  const refresh = async () => {
    const res = await api.get<PurchaseRequestRecord>(`/purchase-requests/${id}`);
    setPr(res.data);
    await refreshRfqs();
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const pickQuotation = () => fileInputRef.current?.click();

  const onQuotationSelected = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    const data = new FormData();
    data.append('file', file);
    setActionError('');
    try {
      await api.post(`/purchase-requests/${id}/documents`, data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await refresh();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Upload failed.');
    }
  };

  const removeQuotation = async (documentId: number) => {
    if (!(await confirmDelete('Remove this quotation?', { confirmLabel: 'Remove' }))) return;
    await api.delete(`/purchase-requests/${id}/documents/${documentId}`);
    await refresh();
  };

  const issueRfq = async (e: FormEvent) => {
    e.preventDefault();
    if (issuingRfq) return;
    setIssuingRfq(true);
    setActionError('');
    try {
      await api.post(`/purchase-requests/${id}/rfq-issuances`, {
        issued_at: new Date().toISOString().slice(0, 10),
        due_date: rfqDueDate || undefined,
      });
      setRfqDueDate('');
      await refreshRfqs();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Could not issue the RFQ.');
    } finally {
      setIssuingRfq(false);
    }
  };

  const setResponseField = (rfqId: number, key: 'supplier_name' | 'quoted_amount') => (e: ChangeEvent<HTMLInputElement>) =>
    setResponseForms((prev) => {
      const current = prev[rfqId] ?? { supplier_name: '', quoted_amount: '' };
      return { ...prev, [rfqId]: { ...current, [key]: e.target.value } };
    });

  const addResponse = async (rfqId: number, e: FormEvent) => {
    e.preventDefault();
    const form = responseForms[rfqId];
    if (!form?.supplier_name || !form?.quoted_amount) return;
    setActionError('');
    try {
      await api.post(`/rfq-issuances/${rfqId}/responses`, {
        supplier_name: form.supplier_name,
        quoted_amount: form.quoted_amount,
        received_at: new Date().toISOString().slice(0, 10),
      });
      setResponseForms((prev) => ({ ...prev, [rfqId]: { supplier_name: '', quoted_amount: '' } }));
      await refreshRfqs();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Could not record the response.');
    }
  };

  const selectResponse = async (responseId: number) => {
    setActionError('');
    try {
      await api.post(`/rfq-responses/${responseId}/select`);
      await refreshRfqs();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Could not select this quote.');
    }
  };

  const submit = async () => {
    setActionError('');
    try {
      await api.post(`/purchase-requests/${id}/submit`);
      await refresh();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Could not submit this request.');
    }
  };

  const decide = async (decision: 'approved' | 'rejected') => {
    setActionError('');
    try {
      await api.post(`/purchase-requests/${id}/decide`, { decision, comment: comment || undefined });
      setComment('');
      await refresh();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Could not record this decision.');
    }
  };

  const remove = async () => {
    if (!pr) return;
    if (!(await confirmDelete(`Delete draft request "${pr.title}"?`))) return;
    try {
      await api.delete(`/purchase-requests/${id}`);
      navigate('/procurement/requests');
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  if (!pr) return null;

  const isAboveThreshold = pr.estimated_cost >= (settings.procurement_quotation_threshold ?? 6000);
  const requiredQuotations = isAboveThreshold ? 3 : 1;
  const rfqResponseCount = rfqs.reduce((sum, rfq) => sum + rfq.responses.length, 0);
  const quotationCount = (pr.documents?.length ?? 0) + rfqResponseCount;
  // Both steps (first/second) are gated purely by procurement.approve —
  // mirrors PurchaseRequestController::decide(). The self-approval guard
  // (a second approver must differ from the first) is enforced server-side.
  const canActOnCurrentStep = can('procurement.approve') && ['pending_approval', 'pending_second_approval'].includes(pr.status);

  return (
    <>
      <header className="page-header">
        <div className="page-header-actions">
          <IconButton icon={IconBack} label="Back to list" onClick={() => navigate('/procurement/requests')} />
          <IconButton
            icon={IconTrash}
            label="Delete draft"
            variant="danger-ghost"
            onClick={remove}
            hidden={pr.status !== 'draft'}
          />
        </div>
      </header>

      {actionError && (
        <div className="alert alert-error" style={{ marginBottom: 16 }}>
          {actionError}
        </div>
      )}

      <div className="card">
        <div className="detail-summary">
          <div>
            <div className="detail-summary-title">{pr.reference}</div>
            <span className={`badge ${statusBadgeClass(pr.status)}`}>{STATUS_LABELS[pr.status] ?? pr.status}</span>
            <div className="detail-summary-amount">{fmtMoney(pr.estimated_cost)}</div>
            <div className="detail-summary-service">{pr.item_name}</div>
          </div>
          {pr.purchase_order && (
            <div>
              <Link className="btn btn-ghost" to={`/procurement/orders/${pr.purchase_order.id}`}>
                View purchase order {pr.purchase_order.po_number}
              </Link>
            </div>
          )}
        </div>

        <div className="detail-grid">
          <div className="detail-item">
            <span className="detail-item-label">Quantity</span>
            <span className="detail-item-value">{pr.quantity}</span>
          </div>
          <div className="detail-item">
            <span className="detail-item-label">Request type</span>
            <span className={`detail-item-value ${pr.request_type ? '' : 'empty'}`}>
              {pr.request_type ? labelFor(lists, 'purchase_request_type', pr.request_type) : 'Not set'}
            </span>
          </div>
          <div className="detail-item">
            <span className="detail-item-label">Requested by</span>
            <span className="detail-item-value">{pr.requester?.full_name}</span>
          </div>
        </div>

        {pr.description && (
          <div className="detail-item detail-item-full">
            <span className="detail-item-label">Description</span>
            <p className="detail-notes">{pr.description}</p>
          </div>
        )}
        {pr.purpose && (
          <div className="detail-item detail-item-full">
            <span className="detail-item-label">Purpose</span>
            <p className="detail-notes">{pr.purpose}</p>
          </div>
        )}
        {pr.final_outcome && (
          <div className="detail-item detail-item-full">
            <span className="detail-item-label">Final outcome</span>
            <span className="detail-item-value">
              {pr.final_outcome}
              {pr.final_comments && <> — &ldquo;{pr.final_comments}&rdquo;</>}
            </span>
          </div>
        )}
      </div>

      {pr.status === 'draft' && (
        <div className="card">
          <h2>Quotations</h2>
          <p className="muted">
            {quotationCount} of {requiredQuotations} required quotation(s) recorded (attached documents and RFQ
            vendor responses count together)
            {isAboveThreshold
              ? ' — this request is at or above the quotation threshold, so 3 quotations and a second, different approver are required.'
              : '.'}
          </p>
          <ul className="simple-list">
            {pr.documents?.map((doc) => (
              <li key={doc.id}>
                <a href={doc.url} target="_blank" rel="noreferrer">
                  {doc.original_name}
                </a>{' '}
                <button type="button" className="btn btn-danger-ghost" onClick={() => removeQuotation(doc.id)}>
                  Remove
                </button>
              </li>
            ))}
            {(!pr.documents || pr.documents.length === 0) && <li className="muted">No quotations attached yet.</li>}
          </ul>
          <button type="button" className="btn btn-ghost" onClick={pickQuotation}>
            + Attach quotation
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf,image/png,image/jpeg,image/webp"
            onChange={onQuotationSelected}
            hidden
          />
          <div className="form-actions">
            <button
              type="button"
              className="btn btn-primary"
              onClick={submit}
              disabled={quotationCount < requiredQuotations}
            >
              Submit for approval
            </button>
          </div>
        </div>
      )}

      {pr.status === 'draft' && (
        <div className="card">
          <h2>Request for Quotation (RFQ)</h2>
          <p className="muted">
            Issue a formal RFQ and record each supplier&apos;s response — a structured alternative to attaching
            quotation documents. Each response counts toward the quotation total above.
          </p>
          {rfqs.map((rfq) => (
            <div key={rfq.id} className="detail-item detail-item-full" style={{ marginBottom: 16 }}>
              <span className="detail-item-label">
                {rfq.rfq_number} — issued {rfq.issued_at}
                {rfq.due_date ? `, due ${rfq.due_date}` : ''}
              </span>
              <div className="table-scroll">
                <table>
                  <thead>
                    <tr>
                      <th>Supplier</th>
                      <th className="right">Quoted Amount</th>
                      <th>Received</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rfq.responses.map((r) => (
                      <tr key={r.id}>
                        <td>{r.supplier?.business_name ?? r.supplier_name ?? '—'}</td>
                        <td className="right">{fmtMoney(Number(r.quoted_amount))}</td>
                        <td className="muted">{r.received_at ?? '—'}</td>
                        <td className="right">
                          {r.is_selected ? (
                            <span className="badge badge-active">Selected</span>
                          ) : (
                            <button type="button" className="btn btn-ghost" onClick={() => selectResponse(r.id)}>
                              Select as winner
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                    {rfq.responses.length === 0 && (
                      <tr>
                        <td colSpan={4} className="muted center">
                          No responses recorded yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <form onSubmit={(e) => addResponse(rfq.id, e)} className="form-grid" style={{ marginTop: 8 }}>
                <label>
                  Supplier name
                  <input
                    value={responseForms[rfq.id]?.supplier_name ?? ''}
                    onChange={setResponseField(rfq.id, 'supplier_name')}
                  />
                </label>
                <label>
                  Quoted amount
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={responseForms[rfq.id]?.quoted_amount ?? ''}
                    onChange={setResponseField(rfq.id, 'quoted_amount')}
                  />
                </label>
                <div className="form-actions">
                  <button type="submit" className="btn btn-ghost">
                    + Add response
                  </button>
                </div>
              </form>
            </div>
          ))}
          {rfqs.length === 0 && <p className="muted">No RFQs issued yet.</p>}
          <form onSubmit={issueRfq} className="form-grid">
            <label>
              Due date (optional)
              <input type="date" value={rfqDueDate} onChange={(e) => setRfqDueDate(e.target.value)} />
            </label>
            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={issuingRfq}>
                {issuingRfq ? 'Issuing…' : '+ Issue RFQ'}
              </button>
            </div>
          </form>
        </div>
      )}

      {pr.status !== 'draft' && (
        <div className="card">
          <h2>Approval history</h2>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Step</th>
                  <th>Approver</th>
                  <th>Decision</th>
                  <th>Comment</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Request</td>
                  <td>{pr.requester?.full_name}</td>
                  <td className="capitalize">Submitted</td>
                  <td className={pr.purpose ? '' : 'muted'}>{pr.purpose || '—'}</td>
                  <td className="muted">{formatDateTime(pr.created_at)}</td>
                </tr>
                {pr.approvals?.map((a) => (
                  <tr key={a.id}>
                    <td>{STEP_LABELS[a.step] ?? a.step}</td>
                    <td>{a.approver?.full_name}</td>
                    <td className="capitalize">{a.decision}</td>
                    <td className={a.comment ? '' : 'muted'}>{a.comment || '—'}</td>
                    <td className="muted">{formatDateTime(a.decided_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {canActOnCurrentStep && (
        <div className="card">
          <h2>Your decision</h2>
          <label className="span-2">
            Comment
            <textarea rows={2} value={comment} onChange={(e) => setComment(e.target.value)} />
          </label>
          <div className="form-actions">
            <button type="button" className="btn btn-danger-ghost" onClick={() => decide('rejected')}>
              Reject
            </button>
            <button type="button" className="btn btn-primary" onClick={() => decide('approved')}>
              Approve
            </button>
          </div>
        </div>
      )}
    </>
  );
}
